import { useState, useEffect, useRef } from "react";
import PhoneInput from "../common/PhoneInput";
import Input from "../form/input/InputField";
import Select from "../form/Select";
import { CreditCard, IdCard, LockIcon, Mail, ShoppingCart, User, X } from "lucide-react";
import Button from "../ui/button/Button";
import axios from "axios";
import valid from 'card-validator';
import CheckoutCard from "../common/CheckoutCard";
import publicApi from "../../services/publicApi";
import TermsAndConditions from "./TermsAndConditions";
import { AsaasFinancialNotice } from "../common/AsaasSeal";
import { INITIAL_INSTALLMENTS_MAX, readInitialInstallments } from "../../utils/initialInstallments";
import { useI18n } from '../../i18n/i18nContext';
import { formatCurrencyValue } from '../../utils/currencyUtils';
import { getCheckoutSessionHeaders } from '../../utils/apiHeaders';
import { getFbc } from '../../utils/pixelTracking';
import { getFacebookCookies } from '../../utils/facebookCookies';
import countryList from '../../utils/countryList';

const countryListForSelect = countryList.filter(({ code }) => !!code);

const countries = [
    // Brasil sempre primeiro
    ...countryListForSelect
        .sort((a, b) => (a.code === 'BR' ? -1 : b.code === 'BR' ? 1 : 0))
        .map(({ code, englishName, localName }) => ({
            code,
            name: localName ? `${englishName} | ${localName}` : englishName,
        })),
];

// Helper para gerar a bandeira a partir do código ISO
const getFlagEmoji = (countryCode: string): string => {
    if (!countryCode || countryCode.length !== 2) return '🌐';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};


interface CheckoutFormData {
    name: string;
    documentNumber: string;
    email: string;
    phone: string;
    installments: string;
    installmentValue?: number; // Valor da parcela
    totalValue?: number; // Valor total
}

interface CheckoutFormProps {
    offerData?: any;
    offerId?: string;
    onSuccess: (data: any) => void;
    onError: (error: any) => void;
    onInstallmentChange?: (installment: string, value: number, isFirst: boolean, total: number) => void;
    clientIp?: string;
    onPaymentProcessing?: () => void;
    couponDiscount?: number; // Prop do cupom de desconto
    discountedInstallmentOptions?: any[]; // Opções de parcelamento com desconto
    appliedCouponData?: any; // Dados completos do cupom aplicado
    selectedOrderBumps?: string[]; // Order bumps selecionados
    onOrderBumpsChange?: (bumps: string[]) => void; // Callback para atualizar os order bumps selecionados
    onCouponApplied?: (couponDiscount: number, couponData: any, installmentOptions?: any[]) => void; // Callback para notificar cupom aplicado
    sessionId?: string; // ID da sessão de checkout
    utmParams?: Record<string, string | boolean | string[]>; // Parâmetros UTM
    installmentValue?: number; // Valor da parcela
    totalValue?: number; // Valor total
}

// Função para formatar valor na moeda da oferta
const formatCurrency = (value: number, currencyCode: string = 'BRL') => {
    // Usamos o formatCurrencyValue do currencyUtils para garantir consistência
    return formatCurrencyValue(value, currencyCode);
};

// Mapeia frequency da API para rótulo em PT-BR
const getFrequencyLabel = (freq?: string): string => {
    if (!freq) return '';
    const f = String(freq).toLowerCase();
    if (['annual', 'year', 'yearly', 'anual', 'ano'].includes(f)) return 'ano';
    if (['semiannual', 'semi-annual', 'semestral', 'semester', 'semestre'].includes(f)) return 'semestre';
    if (['quarterly', 'trimestral', 'quarter', 'trimestre'].includes(f)) return 'trimestre';
    if (['bimonthly', 'bi-monthly', 'bimestral', 'bimestre'].includes(f)) return 'bimestre';
    if (['monthly', 'month', 'mensal', 'mês', 'mes'].includes(f)) return 'mês';
    if (['weekly', 'week', 'semanal', 'semana'].includes(f)) return 'semana';
    if (['daily', 'day', 'diario', 'diária', 'dia'].includes(f)) return 'dia';
    return '';
};

// Função para aplicar máscara de CPF: 000.000.000-00
const applyMaskCPF = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1')
        .substr(0, 14);
};

// Função para aplicar máscara de CNPJ: 00.000.000/0000-00
const applyMaskCNPJ = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1')
        .substr(0, 18);
};

// Função para aplicar máscara de CPF/CNPJ automaticamente
const applyMaskDocument = (value: string) => {
    const numbers = value.replace(/\D/g, '');

    if (numbers.length <= 11) {
        return applyMaskCPF(value);
    } else {
        return applyMaskCNPJ(value);
    }
};

// Função para validar CPF
const validateCPF = (cpf: string) => {
    cpf = cpf.replace(/\D/g, '');

    if (cpf.length !== 11) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cpf)) return false;

    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;

    if (parseInt(cpf.charAt(9)) !== digit1) return false;

    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;

    return parseInt(cpf.charAt(10)) === digit2;
};

// Função para validar CNPJ
const validateCNPJ = (cnpj: string) => {
    cnpj = cnpj.replace(/\D/g, '');

    if (cnpj.length !== 14) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cnpj)) return false;

    // Validação do primeiro dígito verificador
    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    const digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;

    for (let i = size; i >= 1; i--) {
        sum += parseInt(numbers.charAt(size - i)) * pos--;
        if (pos < 2) pos = 9;
    }

    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;

    // Validação do segundo dígito verificador
    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;

    for (let i = size; i >= 1; i--) {
        sum += parseInt(numbers.charAt(size - i)) * pos--;
        if (pos < 2) pos = 9;
    }

    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

    return result === parseInt(digits.charAt(1));
};

// Função para obter uma versão super clara da cor
const getSuperLightColor = (hexColor: string): string => {
    try {
        if (!hexColor || hexColor.length < 7) return 'rgba(0, 0, 0, 0.1)';
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.1)`;
    } catch (error) {
        console.error('Erro ao processar cor:', error);
        return 'rgba(0, 0, 0, 0.1)';
    }
};

const getLightColor = (hexColor: string): string => {
    try {
        if (!hexColor || hexColor.length < 7) return 'rgba(0, 0, 0, 0.3)';
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.3)`;
    } catch (error) {
        console.error('Erro ao processar cor:', error);
        return 'rgba(0, 0, 0, 0.3)';
    }
};

export default function CheckoutForm({
    totalValue,
    installmentValue,
    offerData,
    offerId,
    onSuccess,
    onError,
    onInstallmentChange,
    clientIp,
    onPaymentProcessing,
    couponDiscount = 0,
    discountedInstallmentOptions,
    appliedCouponData,
    selectedOrderBumps = [],
    onOrderBumpsChange,
    onCouponApplied,
    sessionId,
    utmParams
}: CheckoutFormProps) {
    // Obtém a moeda da oferta, com fallback para BRL
    const currencyCode = offerData?.currency || 'BRL';
    // Use o hook de tradução
    const { t } = useI18n();

    // Detectar país pelo IP na montagem do componente
    useEffect(() => {
        const detectCountryByIp = async () => {
            try {
                const response = await axios.get('https://wtfismyip.com/json', {
                    timeout: 4000,
                });

                const countryCode = response.data?.YourFuckingCountryCode as string | undefined;
                const normalizedCode = countryCode?.toUpperCase();

                // Se não veio código ou não está na lista, fallback BR
                const existsInList = normalizedCode
                    ? countries.some((c) => c.code === normalizedCode)
                    : false;

                const finalCode = existsInList ? (normalizedCode as string) : 'BR';

                setSelectedCountryCode(finalCode);
            } catch {
                // Silencioso: se falhar, mantemos BR
                setSelectedCountryCode('BR');
            }
        };

        detectCountryByIp();
    }, []);

    // Estados do componente
    const [formData, setFormData] = useState<CheckoutFormData>({
        name: "",
        documentNumber: "",
        email: "",
        phone: "",
        installments: "1"
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState<string | null>(null);
    const [apiSuccess, setApiSuccess] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [installmentsOptions, setInstallmentsOptions] = useState<{ value: string; label: string }[]>([]);
    const [selectedMethod, setSelectedMethod] = useState<'card' | 'pix' | 'boleto'>('card');
    const [cardNumber, setCardNumber] = useState("");
    const [cardName, setCardName] = useState("");
    const [cardExpiry, setCardExpiry] = useState("");
    const [cardCvv, setCardCvv] = useState("");
    const [selectedBumps, setSelectedBumps] = useState<string[]>(selectedOrderBumps);
    const [isCalculating, setIsCalculating] = useState<boolean>(false);
    const [processingBumpId, setProcessingBumpId] = useState<string | null>(null);
    const [isAddingBump, setIsAddingBump] = useState<boolean>(true);
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('BR'); // código ISO do país selecionado
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState<boolean>(false);
    const [countrySearch, setCountrySearch] = useState<string>('');
    const [isCountryChanging, setIsCountryChanging] = useState<boolean>(false); // Loading ao mudar país

    // Estado para armazenar as opções de parcelamento atualizadas (order bumps/cupons)
    const [updatedInstallmentOptions, setUpdatedInstallmentOptions] = useState<any[] | null>(null);

    // Estados dos termos de uso do vendedor
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);
    const hasCompanyTerms = !!offerData?.companyTerms;

    // Estados do cupom de desconto
    const [couponCode, setCouponCode] = useState<string>('');
    const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
    const [couponLoading, setCouponLoading] = useState<boolean>(false);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [couponSuccess, setCouponSuccess] = useState<string | null>(null);
    const [isCouponOpen, setIsCouponOpen] = useState<boolean>(false);

    // Forçar mudança para cartão quando país não for BR e método for PIX/Boleto
    useEffect(() => {
        if (selectedCountryCode !== 'BR' && (selectedMethod === 'pix' || selectedMethod === 'boleto')) {
            setSelectedMethod('card');
        }
    }, [selectedCountryCode, selectedMethod]);

    // PROTEÇÃO: País internacional sempre 1x (sem parcelamento)
    useEffect(() => {
        if (selectedCountryCode !== 'BR' && formData.installments !== "1") {
            setFormData(prev => ({ ...prev, installments: "1" }));

            // Notificar o componente pai com valor à vista
            const singleOption = offerData?.installmentOptions?.find((opt: any) => opt.number === 1);
            if (singleOption && onInstallmentChangeRef.current) {
                onInstallmentChangeRef.current("1", singleOption.value, true, singleOption.totalAmount);
            }
        }
    }, [selectedCountryCode, formData.installments, offerData?.installmentOptions]);

    // Efeito de loading ao mudar país (skeleton loader)
    useEffect(() => {
        if (isCountryChanging) {
            const timer = setTimeout(() => {
                setIsCountryChanging(false);
            }, 600); // 600ms de skeleton loader
            return () => clearTimeout(timer);
        }
    }, [isCountryChanging]);

    // FUNÇÃO HELPER: Determinar qual fonte de dados usar (prioridade: atualizadas > desconto > normais)
    const getCurrentInstallmentOptions = () => {
        // 1ª prioridade: Opções atualizadas por order bumps/cupons
        if (updatedInstallmentOptions && updatedInstallmentOptions.length > 0) {
            return updatedInstallmentOptions;
        }

        // 2ª prioridade: Opções com desconto de cupom
        if (discountedInstallmentOptions && discountedInstallmentOptions.length > 0) {
            return discountedInstallmentOptions;
        }

        // 3ª prioridade: Opções normais da oferta
        return offerData?.installmentOptions || [];
    };

    // Usar sempre os order bumps da oferta recebida via prop!
    const orderBumps = offerData?.orderBumps || [];

    // Referências para controle de debounce e histórico
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const lastLeadSent = useRef({ email: '', name: '', phone: '' });

    // Referência para rastrear última seleção manual do usuário
    // null = o comprador ainda não escolheu parcela manualmente
    const lastUserSelection = useRef<string | null>(null);

    // Parcela que o produtor quer exibir ao abrir o checkout (offer.settingsData.initialInstallments).
    // null = padrão (maior parcela disponível).
    const getPreferredInitialInstallment = (): number | null => {
        const v = readInitialInstallments(offerData?.settingsData);
        return v === INITIAL_INSTALLMENTS_MAX ? null : parseInt(v, 10);
    };

    // Referência estável para onInstallmentChange para evitar loops infinitos
    const onInstallmentChangeRef = useRef(onInstallmentChange);
    onInstallmentChangeRef.current = onInstallmentChange;

    // LÓGICA ROBUSTA UNIFICADA: Seleção automática da maior parcela disponível
    useEffect(() => {
        // Se está aplicando cupom, aguarda as opções com desconto
        if (discountedInstallmentOptions && discountedInstallmentOptions.length > 0) return;

        // ROBUSTEZ: Validações rigorosas antes de processar
        if (!offerData?.installmentOptions?.length) {
            console.warn('Dados de parcelamento indisponíveis ou inválidos');
            return;
        }

        const installmentOptions = offerData.installmentOptions;
        const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;

        try {
            // Gera opções de exibição
            const options = installmentOptions.map((option: any) => ({
                value: option.number.toString(),
                label: option.number === 1
                    ? isBRL
                        ? `1x de ${formatCurrency(option.totalAmount, currencyCode)}`
                        : formatCurrency(option.totalAmount, currencyCode)
                    : `${option.number}x de ${formatCurrency(option.value, currencyCode)}`
            }));

            setInstallmentsOptions(options);

            // SELEÇÃO AUTOMÁTICA ROBUSTA
            let targetInstallment: any;
            let selectedInstallmentNumber: string;

            // PROTEÇÃO: Se maxInstallments === 1, sempre 1x
            if (offerData?.maxInstallments === 1) {
                targetInstallment = installmentOptions.find((opt: any) => opt.number === 1) || installmentOptions[0];
                selectedInstallmentNumber = "1";
            } else if (isBRL) {
                // Para BRL: só seleciona maior parcela se ainda não tem uma seleção válida
                const currentSelection = formData.installments;
                const currentExists = installmentOptions.find((opt: any) =>
                    opt.number.toString() === currentSelection
                );

                // Prioridade: seleção manual do usuário > seleção atual válida > maior parcela
                const userSelection = lastUserSelection.current;
                const userSelectionExists = installmentOptions.find((opt: any) =>
                    opt.number.toString() === userSelection
                );

                if (userSelection && userSelectionExists) {
                    // 1ª prioridade: Respeita seleção manual do usuário
                    targetInstallment = userSelectionExists;
                    selectedInstallmentNumber = userSelection;
                } else if (currentExists && currentSelection !== "1") {
                    // 2ª prioridade: Mantém seleção atual válida
                    targetInstallment = currentExists;
                    selectedInstallmentNumber = currentSelection;
                } else {
                    // 3ª prioridade (inicialização): parcela configurada na oferta,
                    // ou a maior disponível quando não houver configuração
                    const maxOption = installmentOptions.reduce((max: any, current: any) =>
                        current.number > max.number ? current : max
                    );
                    const preferred = getPreferredInitialInstallment();
                    let preferredOption: any = null;
                    if (preferred !== null) {
                        // Se a parcela configurada não existir (ex.: valor mínimo da parcela),
                        // usa a maior disponível que não passe dela
                        preferredOption = installmentOptions
                            .filter((opt: any) => opt.number <= preferred)
                            .reduce((best: any, current: any) =>
                                !best || current.number > best.number ? current : best, null);
                    }
                    targetInstallment = preferredOption || maxOption;
                    selectedInstallmentNumber = String(targetInstallment.number);
                }
            } else {
                // Para outras moedas: sempre 1x
                targetInstallment = installmentOptions.find((opt: any) => opt.number === 1) || installmentOptions[0];
                selectedInstallmentNumber = "1";
            }

            // VALIDAÇÃO FINAL: Garante que temos dados válidos
            if (!targetInstallment || !targetInstallment.value || !targetInstallment.totalAmount) {
                console.error('Opção de parcelamento inválida:', targetInstallment);
                return;
            }

            // Atualiza o formulário
            setFormData(prev => ({
                ...prev,
                installments: selectedInstallmentNumber
            }));

            // Notifica o componente pai com VALORES VÁLIDOS
            if (onInstallmentChangeRef.current) {
                onInstallmentChangeRef.current(
                    selectedInstallmentNumber,
                    targetInstallment.value,
                    targetInstallment.number === 1,
                    targetInstallment.totalAmount
                );
            }

        } catch (error) {
            console.error('Erro ao processar opções de parcelamento:', error);
            // Fallback de emergência: define 1x com valor base da oferta
            setFormData(prev => ({ ...prev, installments: "1" }));
            if (onInstallmentChangeRef.current && offerData.price) {
                onInstallmentChangeRef.current("1", offerData.price, true, offerData.price);
            }
        }
    }, [offerData, currencyCode]);

    // PROTEÇÃO CRÍTICA: Se maxInstallments === 1, sempre forçar 1x
    useEffect(() => {
        if (offerData?.maxInstallments === 1 && formData.installments !== "1") {
            console.log('⚠️ maxInstallments === 1 detectado, forçando parcela para 1x');

            setFormData(prev => ({
                ...prev,
                installments: "1"
            }));

            // Notificar o componente pai com valor à vista
            const singleOption = offerData.installmentOptions?.find((opt: any) => opt.number === 1);
            if (singleOption && onInstallmentChangeRef.current) {
                onInstallmentChangeRef.current("1", singleOption.value, true, singleOption.totalAmount);
            }
        }
    }, [offerData?.maxInstallments, formData.installments, offerData?.installmentOptions]);

    // LÓGICA ROBUSTA: Parcelamento com desconto aplicado
    useEffect(() => {
        if (!discountedInstallmentOptions?.length) {
            // Se não há mais opções com desconto, limpar as opções atualizadas
            // para que volte a usar as opções normais
            setUpdatedInstallmentOptions(null);
            return;
        }

        const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;

        try {
            // Gera opções de exibição
            const newOptions = discountedInstallmentOptions.map((option: any) => {
                if (!option.number || !option.value || !option.totalAmount) {
                    throw new Error(`Opção de parcelamento inválida: ${JSON.stringify(option)}`);
                }

                return {
                    value: option.number.toString(),
                    label: option.number === 1
                        ? isBRL
                            ? `1x de ${formatCurrency(option.totalAmount, currencyCode)}`
                            : formatCurrency(option.totalAmount, currencyCode)
                        : `${option.number}x de ${formatCurrency(option.value, currencyCode)}`
                };
            });

            setInstallmentsOptions(newOptions);

            // SELEÇÃO AUTOMÁTICA ROBUSTA PARA DESCONTO
            let targetOption: any;
            let selectedInstallmentNumber: string;

            if (isBRL) {
                // Para BRL: mantém seleção atual se válida, senão pega a maior
                const currentExists = discountedInstallmentOptions.find(
                    opt => opt.number.toString() === formData.installments
                );

                if (currentExists) {
                    targetOption = currentExists;
                    selectedInstallmentNumber = formData.installments;
                } else {
                    // Pega a maior parcela disponível
                    targetOption = discountedInstallmentOptions.reduce((max: any, current: any) =>
                        current.number > max.number ? current : max
                    );
                    selectedInstallmentNumber = String(targetOption.number);
                }
            } else {
                // Para outras moedas: sempre 1x
                targetOption = discountedInstallmentOptions.find(opt => opt.number === 1) || discountedInstallmentOptions[0];
                selectedInstallmentNumber = "1";
            }

            // VALIDAÇÃO FINAL
            if (!targetOption) {
                throw new Error('Nenhuma opção válida encontrada');
            }

            // Atualiza o formulário
            setFormData(prev => ({
                ...prev,
                installments: selectedInstallmentNumber
            }));

            // Notifica o componente pai
            if (onInstallmentChangeRef.current) {
                onInstallmentChangeRef.current(
                    selectedInstallmentNumber,
                    targetOption.value,
                    targetOption.number === 1,
                    targetOption.totalAmount
                );
            }

        } catch (error) {
            console.error('Erro ao processar opções com desconto:', error);
            // Fallback: volta para as opções normais
            setFormData(prev => ({ ...prev, installments: "1" }));
        }
    }, [discountedInstallmentOptions, currencyCode]);

    // EFEITO DE VALIDAÇÃO CONTÍNUA: Garante que sempre existe uma parcela válida
    useEffect(() => {
        // ROBUSTEZ: Validações essenciais
        if (!offerData?.installmentOptions?.length || !installmentsOptions?.length) {
            return;
        }

        const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;

        // PROTEÇÃO CRÍTICA: PIX e Boleto sempre devem ser 1x
        if (selectedMethod === 'pix' || selectedMethod === 'boleto') {
            if (formData.installments !== "1") {
                console.warn('Forçando 1x para PIX/Boleto');
                setFormData(prev => ({ ...prev, installments: "1" }));

                // Notifica com valor à vista
                const singleOption = offerData.installmentOptions.find((opt: any) => opt.number === 1);
                if (singleOption && onInstallmentChangeRef.current) {
                    onInstallmentChangeRef.current("1", singleOption.value, true, singleOption.totalAmount);
                }
            }
            return; // Sai da função, não executa resto da validação
        }

        // Verifica se a seleção atual é válida (só para cartão)
        const currentSelectionExists = installmentsOptions.some(opt => opt.value === formData.installments);

        if (!currentSelectionExists) {
            console.warn('Seleção de parcela inválida detectada, corrigindo...', {
                current: formData.installments,
                available: installmentsOptions.map(o => o.value)
            });

            let fallbackOption: any;
            let fallbackInstallmentNumber: string;

            if (isBRL) {
                // Para BRL: sempre a maior parcela disponível
                const maxOptionValue = installmentsOptions[installmentsOptions.length - 1]?.value;
                fallbackOption = offerData.installmentOptions.find((opt: any) => String(opt.number) === maxOptionValue);
                fallbackInstallmentNumber = maxOptionValue || "1";
            } else {
                // Para outras moedas: sempre 1x
                fallbackOption = offerData.installmentOptions.find((opt: any) => opt.number === 1) || offerData.installmentOptions[0];
                fallbackInstallmentNumber = "1";
            }

            // ÚLTIMA VALIDAÇÃO: Garante que temos dados válidos
            if (fallbackOption && fallbackOption.value && fallbackOption.totalAmount) {
                setFormData(prev => ({ ...prev, installments: fallbackInstallmentNumber }));

                if (onInstallmentChangeRef.current) {
                    onInstallmentChangeRef.current(
                        fallbackInstallmentNumber,
                        fallbackOption.value,
                        fallbackOption.number === 1,
                        fallbackOption.totalAmount
                    );
                }
            } else {
                console.error('Fallback de parcela também inválido, usando emergência');
                setFormData(prev => ({ ...prev, installments: "1" }));
                if (onInstallmentChangeRef.current && offerData.price) {
                    onInstallmentChangeRef.current("1", offerData.price, true, offerData.price);
                }
            }
        }
    }, [installmentsOptions, formData.installments, offerData, selectedMethod]);

    // Função central para o lead (criar/atualizar carrinho) do checkout
    useEffect(() => {
        // Só dispara se já tem um e-mail válido (não faz sentido sem e-mail)
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return;

        // Detecta mudanças reais
        const changed =
            formData.email !== lastLeadSent.current.email ||
            formData.name !== lastLeadSent.current.name ||
            formData.phone !== lastLeadSent.current.phone;

        if (!changed) return;

        // Debounce para evitar flood de requests a cada digitação
        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            // Atualiza controle para não enviar múltiplos iguais
            lastLeadSent.current = {
                email: formData.email,
                name: formData.name,
                phone: formData.phone
            };

            axios.post(`${import.meta.env.VITE_API_URL}/api/cart`, {
                offerId,
                companyId: offerData?.product?.company?.id || 'null',
                email: formData.email,
                sessionId: sessionId || localStorage.getItem(`sessionId_${offerId}`),
                name: formData.name,
                phone: formData.phone,
                status: 'open',
                documentNumber: formData.documentNumber,
                utmFields: utmParams,
                checkoutUrl: window.location.href,
                items: [
                    {
                        productId: offerData?.product?.id,
                        title: offerData?.name || 'Produto',
                        quantity: 1,
                        price: offerData?.price || 0,
                        id: offerData?.id || offerId
                    }
                ]
            });
        }, 800); // 800ms debounce
    }, [formData.email, formData.name, formData.phone]);


    //Para o checkout pré-populado
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const name = params.get('name');
        const email = params.get('email');
        const phone = params.get('phone');
        const documentNumber = params.get('documentNumber');

        // Atualiza o formData normalmente
        setFormData(prev => {
            const maskedDocument = documentNumber ? applyMaskDocument(documentNumber) : '';
            const newFormData = {
                ...prev,
                name: name || '',
                email: email || '',
                phone: phone || '',
                documentNumber: maskedDocument || '',
            };

            if (name) validateName(name);
            if (email) validateEmail(email);
            if (documentNumber) validateDocument(documentNumber);

            return newFormData;
        });

        // Se o email já veio preenchido E é válido, já salva!
        if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            axios.post(`${import.meta.env.VITE_API_URL}/api/cart`, {
                offerId,
                companyId: offerData?.product?.company?.id || 'null',
                email,
                sessionId: sessionId || localStorage.getItem(`sessionId_${offerId}`),
                name,
                phone,
                documentNumber,
                status: 'open',
                utmFields: utmParams,
                checkoutUrl: window.location.href,
                items: [
                    {
                        productId: offerData?.product?.id,
                        title: offerData?.name || 'Produto',
                        quantity: 1,
                        price: offerData?.price || 0,
                        id: offerData?.id || offerId
                    }
                ]
            });
        }
        // eslint-disable-next-line
    }, []);

    const toggleBumpSelection = async (bumpId: string) => {
        // Verificar se estamos adicionando ou removendo o bump
        const isAdding = !selectedBumps.includes(bumpId);
        setIsAddingBump(isAdding);

        // Atualizar a seleção local de bumps
        const newSelectedBumps = isAdding
            ? [...selectedBumps, bumpId]
            : selectedBumps.filter(id => id !== bumpId);

        setSelectedBumps(newSelectedBumps);

        // Notificar o componente pai sobre a mudança nos order bumps selecionados
        if (onOrderBumpsChange) {
            onOrderBumpsChange(newSelectedBumps);
        }

        setIsCalculating(true);
        setProcessingBumpId(bumpId);

        // Usar setTimeout para garantir que o loader fique visível por pelo menos 1 segundo
        setTimeout(async () => {
            try {
                // Chamar a API para calcular os valores atualizados
                const response = await publicApi.post('/api/checkout/calculate', {
                    offerId: offerId,
                    selectedBumps: newSelectedBumps,
                    couponCode: appliedCouponData?.code || '',
                    installments: parseInt(formData.installments)
                });

                if (response.data.success) {
                    // Atualizar os dados da oferta com os valores calculados pelo backend

                    // Atualizar as opções de parcelamento
                    if (response.data.updatedInstallments) {
                        const newInstallmentOptions = response.data.updatedInstallments;

                        // CORREÇÃO: Salvar as opções atualizadas no estado local
                        setUpdatedInstallmentOptions(newInstallmentOptions);

                        // Mapear as opções de parcelamento para o formato esperado pelo componente
                        const formattedOptions = newInstallmentOptions.map((option: any) => {
                            const installmentNumber = option.number;
                            const installmentValue = option.value;
                            const totalAmount = option.totalAmount;

                            // Para a primeira parcela (à vista)
                            if (installmentNumber === 1) {
                                const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;
                                return {
                                    value: installmentNumber.toString(),
                                    label: isBRL
                                        ? `1x de ${formatCurrency(totalAmount, currencyCode)}`
                                        : formatCurrency(totalAmount, currencyCode)
                                };
                            }

                            // Para as demais parcelas
                            return {
                                value: installmentNumber.toString(),
                                label: `${installmentNumber}x de ${formatCurrency(installmentValue)}`
                            };
                        });

                        // Atualizar as opções no estado
                        setInstallmentsOptions(formattedOptions);

                        // Atualizar o valor da parcela selecionada
                        const selectedInstallmentOption = newInstallmentOptions.find(
                            (option: any) => option.number === parseInt(formData.installments)
                        );

                        if (selectedInstallmentOption) {
                            // Notificar o componente pai sobre a mudança no valor da parcela
                            if (onInstallmentChange) {
                                onInstallmentChange(
                                    formData.installments,
                                    selectedInstallmentOption.value,
                                    selectedInstallmentOption.number === 1,
                                    selectedInstallmentOption.totalAmount
                                );
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Erro ao atualizar valores com order bump:', error);
            } finally {
                setIsCalculating(false);
                setProcessingBumpId(null);
            }
        }, 1000); // Garantir que o loader fique visível por pelo menos 1 segundo
    };

    // Ref para cancelar requests pendentes
    const installmentChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const installmentCalculationControllerRef = useRef<AbortController | null>(null);

    const handleInstallmentsChange = async (value: string) => {
        // Verificar se o valor realmente mudou para evitar processamento desnecessário
        if (formData.installments === value) return;

        // Registrar esta mudança como seleção manual do usuário
        lastUserSelection.current = value;

        // Atualizar o valor no formulário usando função callback para garantir estado atual
        setFormData(prev => ({
            ...prev,
            installments: value
        }));

        // Limpar erro de parcelas se um valor for selecionado
        if (errors.installments && value) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.installments;
                return newErrors;
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Se não temos offerData, não fazer nada
        if (!offerData) return;

        // Cancelar timeout anterior se existir
        if (installmentChangeTimeoutRef.current) {
            clearTimeout(installmentChangeTimeoutRef.current);
        }

        // Cancelar request anterior se existir
        if (installmentCalculationControllerRef.current) {
            installmentCalculationControllerRef.current.abort();
        }

        // Debounce para evitar múltiplas chamadas API
        installmentChangeTimeoutRef.current = setTimeout(async () => {
            // Verificar novamente se o valor ainda é o mesmo (evita race conditions)
            if (formData.installments !== value) return;

            setIsCalculating(true);

            // Criar novo controller para cancelamento
            installmentCalculationControllerRef.current = new AbortController();

            try {
                // Chamar a API para calcular os valores atualizados com a nova opção de parcelamento
                const response = await publicApi.post('/api/checkout/calculate', {
                    offerId: offerId,
                    selectedBumps: selectedBumps,
                    couponCode: appliedCouponData?.code || '',
                    installments: parseInt(value)
                }, {
                    signal: installmentCalculationControllerRef.current.signal
                });

                // Verificar se o request foi cancelado
                if (installmentCalculationControllerRef.current.signal.aborted) {
                    return;
                }

                if (response.data.success && response.data.updatedInstallments) {
                    const newInstallmentOptions = response.data.updatedInstallments;

                    // CORREÇÃO: Salvar as opções atualizadas no estado local
                    setUpdatedInstallmentOptions(newInstallmentOptions);

                    // Mapear as opções de parcelamento para o formato esperado pelo componente
                    const formattedOptions = newInstallmentOptions.map((option: any) => {
                        const installmentNumber = option.number;
                        const installmentValue = option.value;
                        const totalAmount = option.totalAmount;

                        // Para a primeira parcela (à vista)
                        if (installmentNumber === 1) {
                            const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;
                            return {
                                value: installmentNumber.toString(),
                                label: isBRL
                                    ? `1x de ${formatCurrency(totalAmount, currencyCode)}`
                                    : formatCurrency(totalAmount, currencyCode)
                            };
                        }

                        // Para as demais parcelas
                        return {
                            value: installmentNumber.toString(),
                            label: `${installmentNumber}x de ${formatCurrency(installmentValue, currencyCode)}`
                        };
                    });

                    // Atualizar as opções no estado
                    setInstallmentsOptions(formattedOptions);

                    // Encontrar a opção de parcelamento selecionada
                    const installmentNumber = parseInt(value);
                    const selectedOption = newInstallmentOptions.find(
                        (option: any) => option.number === installmentNumber
                    );

                    // Notificar o componente pai sobre a mudança no valor da parcela
                    if (onInstallmentChangeRef.current && selectedOption) {
                        onInstallmentChangeRef.current(
                            value,
                            selectedOption.value,
                            selectedOption.number === 1,
                            selectedOption.totalAmount
                        );
                    }
                }
            } catch (error: any) {
                // Ignorar erros de cancelamento
                if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                    return;
                }
                console.error('Erro ao atualizar valores com nova opção de parcelamento:', error);
            } finally {
                setIsCalculating(false);
                installmentCalculationControllerRef.current = null;
            }
        }, 300); // 300ms debounce
    };

    const handleMethodSelect = (method: 'card' | 'pix' | 'boleto') => {
        if (method === selectedMethod) return;

        setSelectedMethod(method);

        // PROTEÇÃO REFORÇADA: PIX e Boleto sempre 1x
        if (method === 'pix' || method === 'boleto') {

            // Força parcela para 1x IMEDIATAMENTE
            setFormData(prev => ({
                ...prev,
                installments: "1"
            }));

            // Recalcular com API para garantir valores atualizados
            setIsCalculating(true);

            // Chamar API para recalcular com 1 parcela
            publicApi.post('/api/checkout/calculate', {
                offerId: offerId,
                selectedBumps: selectedBumps,
                couponCode: appliedCouponData?.code || '',
                installments: 1
            })
                .then(response => {
                    if (response.data.success) {
                        // CORREÇÃO: Salvar as opções atualizadas no estado local
                        if (response.data.updatedInstallments) {
                            setUpdatedInstallmentOptions(response.data.updatedInstallments);
                        }

                        // Para PIX/Boleto, criar apenas opção 1x
                        const singleInstallment = response.data.updatedInstallments?.find((opt: any) => opt.number === 1);

                        if (singleInstallment) {
                            const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;
                            const singleOption = {
                                value: "1",
                                label: isBRL
                                    ? `1x de ${formatCurrency(singleInstallment.totalAmount, currencyCode)}`
                                    : formatCurrency(singleInstallment.totalAmount, currencyCode)
                            };

                            // Atualiza para mostrar APENAS opção à vista
                            setInstallmentsOptions([singleOption]);

                            // Garante que formData está em 1x
                            setFormData(prev => ({ ...prev, installments: "1" }));

                            // Notifica o componente pai
                            if (onInstallmentChangeRef.current) {
                                onInstallmentChangeRef.current("1", singleInstallment.value, true, singleInstallment.totalAmount);
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error('Erro ao recalcular valores:', error);
                    // Fallback de emergência
                    const singleOption = offerData?.installmentOptions?.find((opt: any) => opt.number === 1);
                    if (singleOption) {
                        setFormData(prev => ({ ...prev, installments: "1" }));
                        if (onInstallmentChangeRef.current) {
                            onInstallmentChangeRef.current("1", singleOption.value, true, singleOption.totalAmount);
                        }
                    }
                })
                .finally(() => {
                    setIsCalculating(false);
                });
        }
        // Se mudou para CARTÃO: restaura todas as opções de parcelamento
        else if (method === 'card') {

            // Recalcula para mostrar todas as opções de parcelamento
            setIsCalculating(true);

            publicApi.post('/api/checkout/calculate', {
                offerId: offerId,
                selectedBumps: selectedBumps,
                couponCode: appliedCouponData?.code || '',
                installments: parseInt(formData.installments) || 1
            })
                .then(response => {
                    if (response.data.success && response.data.updatedInstallments) {
                        // CORREÇÃO: Salvar as opções atualizadas no estado local
                        setUpdatedInstallmentOptions(response.data.updatedInstallments);

                        // Mapear todas as opções de parcelamento
                        const formattedOptions = response.data.updatedInstallments.map((option: any) => {
                            const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;
                            return {
                                value: option.number.toString(),
                                label: option.number === 1
                                    ? isBRL
                                        ? `1x de ${formatCurrency(option.totalAmount, currencyCode)}`
                                        : formatCurrency(option.totalAmount, currencyCode)
                                    : `${option.number}x de ${formatCurrency(option.value, currencyCode)}`
                            };
                        });

                        setInstallmentsOptions(formattedOptions);

                        // Se estava em 1x (vindo de PIX/Boleto), muda para maior parcela
                        if (formData.installments === "1" && formattedOptions.length > 1) {
                            const maxInstallment = formattedOptions[formattedOptions.length - 1];
                            const maxOption = response.data.updatedInstallments.find((opt: any) => opt.number.toString() === maxInstallment.value);

                            if (maxOption) {
                                setFormData(prev => ({ ...prev, installments: maxInstallment.value }));

                                if (onInstallmentChangeRef.current) {
                                    onInstallmentChangeRef.current(
                                        maxInstallment.value,
                                        maxOption.value,
                                        maxOption.number === 1,
                                        maxOption.totalAmount
                                    );
                                }
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error('Erro ao restaurar opções de parcelamento:', error);
                })
                .finally(() => {
                    setIsCalculating(false);
                });
        }
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(email);

        if (!isValidEmail && email.trim() !== '') {
            setErrors({
                ...errors,
                email: t('checkout.errors.invalidEmail')
            });
        } else {
            const newErrors = { ...errors };
            delete newErrors.email;
            setErrors(newErrors);
        }

        return isValidEmail;
    };

    // Função para validar o nome
    const validateName = (name: string) => {
        // Verifica se o campo está vazio
        if (!name.trim()) {
            setErrors({
                ...errors,
                inputName: t('checkout.errors.requiredName')
            });
            return false;
        }

        // Verifica se tem pelo menos um sobrenome (espaço no texto)
        if (!name.trim().includes(' ')) {
            setErrors({
                ...errors,
                inputName: t('checkout.errors.invalidName')
            });
            return false;
        }

        // Verifica se tem pelo menos 3 caracteres
        if (name.trim().length < 3) {
            setErrors({
                ...errors,
                inputName: t('checkout.errors.invalidName')
            });
            return false;
        }

        // Se passou por todas as validações, limpa o erro
        const newErrors = { ...errors };
        delete newErrors.inputName;
        setErrors(newErrors);
        return true;
    };

    // Função para validar documento (CPF/CNPJ)
    const validateDocument = (document: string) => {
        // Remover máscara para validação
        const cleanDocument = document.replace(/\D/g, '');

        // Verifica se o campo está vazio
        if (!cleanDocument) {
            setErrors({
                ...errors,
                inputDocumentNumber: t('checkout.errors.requiredDocument')
            });
            return false;
        }

        // Verifica se é CPF (11 dígitos) ou CNPJ (14 dígitos)
        if (cleanDocument.length === 11) {
            // Validar CPF
            if (!validateCPF(cleanDocument)) {
                setErrors({
                    ...errors,
                    inputDocumentNumber: t('checkout.errors.invalidCPF')
                });
                return false;
            }
        } else if (cleanDocument.length === 14) {
            // Validar CNPJ
            if (!validateCNPJ(cleanDocument)) {
                setErrors({
                    ...errors,
                    inputDocumentNumber: t('checkout.errors.invalidCNPJ')
                });
                return false;
            }
        } else {
            // Número de dígitos inválido
            setErrors({
                ...errors,
                inputDocumentNumber: t('checkout.errors.invalidDocument')
            });
            return false;
        }

        // Se passou por todas as validações, limpa o erro
        const newErrors = { ...errors };
        delete newErrors.inputDocumentNumber;
        setErrors(newErrors);
        return true;
    };

    const validatePhone = (phone: string) => {
        // Remove caracteres não numéricos
        let cleaned = phone.replace(/\D/g, "");

        // Detecta se o número é do Brasil pelo DDI 55
        const isBrazil = cleaned.startsWith("55");

        if (isBrazil) {
            // Remove DDI do Brasil se presente
            if (cleaned.length > 11) {
                cleaned = cleaned.substring(2);
            }

            // Celular BR: (11) 99999-9999
            const isMobile = cleaned.length === 11 && /^([1-9]{2})9[0-9]{8}$/.test(cleaned);
            // Fixo BR: (11) 2345-6789
            const isLandline = cleaned.length === 10 && /^([1-9]{2})[2-8][0-9]{7}$/.test(cleaned);

            const isValidPhone = isMobile || isLandline;

            if (!isValidPhone && cleaned !== "") {
                setErrors({
                    ...errors,
                    phone: t('checkout.errors.invalidPhone')
                });
            } else {
                const newErrors = { ...errors };
                delete newErrors.phone;
                setErrors(newErrors);
            }

            return isValidPhone;
        }

        // Países NÃO-BR: não bloquear o usuário
        // Aceita qualquer número não vazio (mínimo 4 dígitos para evitar envios acidentais)
        const isValidIntl = cleaned.length >= 4;

        // Não exibe erro para internacionais; apenas limpa o erro, se houver
        const newErrors = { ...errors };
        delete newErrors.phone;
        setErrors(newErrors);

        return isValidIntl;
    };

    // Função para aplicar máscara ao número do cartão (separar de 4 em 4 dígitos)
    const applyCardNumberMask = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{4})(?=\d)/g, '$1 ')
            .trim()
            .substr(0, 19); // 16 dígitos + 3 espaços
    };

    // Função para aplicar máscara à data de expiração (MM/AA)
    const applyExpiryDateMask = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{2})(\d)/, '$1/$2')
            .substr(0, 5);
    };

    // Função para validar o número do cartão
    const validateCardNumber = (number: string) => {
        const validation = valid.number(number);

        if (!number.trim()) {
            setErrors({
                ...errors,
                cardNumber: t('checkout.errors.requiredCardNumber')
            });
            return false;
        }

        if (!validation.isValid) {
            setErrors({
                ...errors,
                cardNumber: t('checkout.errors.invalidCardNumber')
            });
            return false;
        }

        // Se passou por todas as validações, limpa o erro
        const newErrors = { ...errors };
        delete newErrors.cardNumber;
        setErrors(newErrors);
        return true;
    };

    // Função para validar o nome no cartão
    const validateCardName = (name: string) => {
        const validation = valid.cardholderName(name);

        if (!name.trim()) {
            setErrors({
                ...errors,
                cardName: t('checkout.errors.requiredCardName')
            });
            return false;
        }

        if (!validation.isValid) {
            setErrors({
                ...errors,
                cardName: t('checkout.errors.invalidCardName')
            });
            return false;
        }

        // Se passou por todas as validações, limpa o erro
        const newErrors = { ...errors };
        delete newErrors.cardName;
        setErrors(newErrors);
        return true;
    };

    // Função para validar a data de expiração
    const validateCardExpiry = (expiry: string) => {
        const validation = valid.expirationDate(expiry);

        if (!expiry.trim()) {
            setErrors({
                ...errors,
                cardExpiry: t('checkout.errors.requiredCardExpiry')
            });
            return false;
        }

        if (!validation.isValid) {
            setErrors({
                ...errors,
                cardExpiry: t('checkout.errors.invalidCardExpiry')
            });
            return false;
        }

        // Se passou por todas as validações, limpa o erro
        const newErrors = { ...errors };
        delete newErrors.cardExpiry;
        setErrors(newErrors);
        return true;
    };

    // Função para validar o CVV
    const validateCardCvv = (cvv: string) => {
        if (!cvv.trim()) {
            setErrors({
                ...errors,
                cardCvv: t('checkout.errors.requiredCVV')
            });
            return false;
        }

        if (cvv.length < 3 || cvv.length > 4) {
            setErrors({
                ...errors,
                cardCvv: t('checkout.errors.invalidCVV')
            });
            return false;
        }

        // Se passou por todas as validações, limpa o erro
        const newErrors = { ...errors };
        delete newErrors.cardCvv;
        setErrors(newErrors);
        return true;
    };

    // Handlers para os campos do cartão
    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maskedValue = applyCardNumberMask(value);
        setCardNumber(maskedValue);

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleCardNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCardName(value);

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleCardExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maskedValue = applyExpiryDateMask(value);
        setCardExpiry(maskedValue);

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleCardCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 4) {
            setCardCvv(value);
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Validação dos campos do cartão no blur
    const handleCardNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateCardNumber(e.target.value);
    };

    const handleCardNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateCardName(e.target.value);
    };

    const handleCardExpiryBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateCardExpiry(e.target.value);
    };

    const handleCardCvvBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateCardCvv(e.target.value);
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData({
            ...formData,
            name: value
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Handler para mudança no campo de documento (CPF/CNPJ)
    const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maskedValue = applyMaskDocument(value);

        setFormData({
            ...formData,
            documentNumber: maskedValue
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Validação de documento no blur para melhor experiência do usuário
    const handleDocumentBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateDocument(e.target.value);
    };

    const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validatePhone(e.target.value);
    };

    const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateName(e.target.value);
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData({
            ...formData,
            email: value
        });
        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Validação de email no blur para melhor experiência do usuário
    const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // InitiateCheckout não sai mais daqui: dispara ao abrir o checkout (Checkout.tsx)
        validateEmail(e.target.value);
    };

    // Função para aplicar/remover cupom de desconto
    const handleApplyCoupon = async () => {
        // Se já tem cupom aplicado, remove
        if (appliedCoupon) {
            try {
                setCouponLoading(true);
                setCouponError(null);
                setCouponSuccess(null);

                const response = await publicApi.post('/api/checkout/calculate', {
                    offerId: offerId,
                    selectedBumps: selectedBumps,
                    installments: parseInt(formData.installments || "1")
                });

                if (response.data.success) {
                    setAppliedCoupon(null);
                    setCouponCode('');
                    setCouponSuccess(t('checkout.removeCouponSuccess'));

                    // Atualiza as opções de parcelamento (remove desconto)
                    if (response.data.updatedInstallments) {
                        setUpdatedInstallmentOptions(response.data.updatedInstallments);
                    }

                    // Notifica o componente pai sobre remoção do cupom
                    if (onCouponApplied) {
                        onCouponApplied(0, null, response.data.updatedInstallments);
                    }

                    // Notifica o componente pai para remover desconto
                    if (onInstallmentChangeRef.current) {
                        const currentOption = response.data.updatedInstallments?.find(
                            (opt: any) => opt.number === parseInt(formData.installments || "1")
                        );
                        if (currentOption) {
                            onInstallmentChangeRef.current(
                                formData.installments,
                                currentOption.value,
                                true,
                                currentOption.totalAmount
                            );
                        }
                    }
                } else {
                    setCouponError(response.data.error || t('checkout.removeCouponError'));
                }
            } catch (error: any) {
                setCouponError(error.response?.data?.error || t('checkout.removeCouponError'));
            } finally {
                setCouponLoading(false);
            }
            return;
        }

        // Validações antes de aplicar
        if (!couponCode.trim()) {
            setCouponError(t('checkout.couponInsert'));
            return;
        }
        if (!offerId) {
            setCouponError(t('checkout.couponInvalidate'));
            return;
        }

        try {
            setCouponLoading(true);
            setCouponError(null);
            setCouponSuccess(null);

            const response = await publicApi.post('/api/checkout/calculate', {
                couponCode: couponCode.trim(),
                offerId: offerId,
                selectedBumps: selectedBumps,
                installments: parseInt(formData.installments || "1")
            });

            if (response.data.success) {
                if (!response.data.couponData) {
                    setCouponError(t('checkout.couponInvalid'));
                    setAppliedCoupon(null);
                    return;
                }

                const couponData = response.data.couponData;
                const couponDiscount = response.data.couponDiscount || 0;

                if (couponDiscount <= 0) {
                    setCouponError(t('checkout.couponNoDiscount'));
                    setAppliedCoupon(null);
                    return;
                }

                // Aplica o cupom
                setAppliedCoupon(couponData);
                setCouponSuccess(t('checkout.couponAppliedSuccess'));

                // Atualiza as opções de parcelamento com desconto
                if (response.data.updatedInstallments) {
                    setUpdatedInstallmentOptions(response.data.updatedInstallments);
                }

                // Notifica o componente pai sobre aplicação do cupom
                if (onCouponApplied) {
                    onCouponApplied(couponDiscount, couponData, response.data.updatedInstallments);
                }

                // Notifica o componente pai com os novos valores
                if (onInstallmentChangeRef.current) {
                    const currentOption = response.data.updatedInstallments?.find(
                        (opt: any) => opt.number === parseInt(formData.installments || "1")
                    );
                    if (currentOption) {
                        onInstallmentChangeRef.current(
                            formData.installments,
                            currentOption.value,
                            true,
                            currentOption.totalAmount
                        );
                    }
                }
            } else {
                setCouponError(response.data.error || t('checkout.couponInvalid'));
                setAppliedCoupon(null);
            }
        } catch (error: any) {
            setCouponError(error.response?.data?.error || t('checkout.couponInvalidate'));
            setAppliedCoupon(null);
        } finally {
            setCouponLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Função auxiliar para adicionar erro e manter referência ao elemento
        const addError = (fieldId: string, message: string) => {
            newErrors[fieldId] = message;
            return fieldId; // Retorna o ID para rastrear o primeiro erro
        };

        // Array para armazenar IDs dos campos com erro na ordem que foram validados
        const errorFields: string[] = [];

        // Validação do nome
        if (!formData.name) {
            errorFields.push(addError('inputName', t('checkout.errors.requiredName')));
        } else if (!formData.name.includes(' ') || formData.name.trim().length < 3) {
            errorFields.push(addError('inputName', t('checkout.errors.invalidName')));
        }

        // Validação do documento (CPF/CNPJ) - apenas para Brasil
        if (selectedCountryCode === 'BR') {
            const cleanDocument = formData.documentNumber.replace(/\D/g, '');
            if (!cleanDocument) {
                errorFields.push(addError('inputDocumentNumber', t('checkout.errors.requiredDocument')));
            } else if (cleanDocument.length === 11) {
                if (!validateCPF(cleanDocument)) {
                    errorFields.push(addError('inputDocumentNumber', t('checkout.errors.invalidCPF')));
                }
            } else if (cleanDocument.length === 14) {
                if (!validateCNPJ(cleanDocument)) {
                    errorFields.push(addError('inputDocumentNumber', t('checkout.errors.invalidCNPJ')));
                }
            } else {
                errorFields.push(addError('inputDocumentNumber', t('checkout.errors.invalidDocument')));
            }
        }

        // Validação do email
        if (!formData.email) {
            errorFields.push(addError('email', t('checkout.errors.requiredEmail')));
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errorFields.push(addError('email', t('checkout.errors.invalidEmailFormat')));
        }

        // Validação do telefone
        if (!formData.phone) {
            errorFields.push(addError('phone', t('checkout.errors.requiredPhone')));
        } else if (!validatePhone(formData.phone)) {
            errorFields.push(addError('phone', t('checkout.errors.invalidPhone')));
        }

        // Validar campos do cartão se for pagamento com cartão
        if (selectedMethod === 'card') {
            // Validar número do cartão
            if (!cardNumber) {
                errorFields.push(addError('cardNumber', t('checkout.errors.requiredCardNumber')));
            } else {
                const numberValidation = valid.number(cardNumber);
                if (!numberValidation.isValid) {
                    errorFields.push(addError('cardNumber', t('checkout.errors.invalidCardNumber')));
                }
            }

            // Validar nome no cartão
            if (!cardName) {
                errorFields.push(addError('cardName', t('checkout.errors.requiredCardName')));
            } else {
                const nameValidation = valid.cardholderName(cardName);
                if (!nameValidation.isValid) {
                    errorFields.push(addError('cardName', t('checkout.errors.invalidCardName')));
                }
            }

            // Validar data de expiração
            if (!cardExpiry) {
                errorFields.push(addError('cardExpiry', t('checkout.errors.requiredCardExpiry')));
            } else {
                const expiryValidation = valid.expirationDate(cardExpiry);
                if (!expiryValidation.isValid) {
                    errorFields.push(addError('cardExpiry', t('checkout.errors.invalidCardExpiry')));
                }
            }

            // Validar CVV
            if (!cardCvv) {
                errorFields.push(addError('cardCvv', t('checkout.errors.requiredCVV')));
            } else if (cardCvv.length < 3 || cardCvv.length > 4) {
                errorFields.push(addError('cardCvv', t('checkout.errors.invalidCVV')));
            }
        }

        // Validar parcelas apenas para moeda BRL e país Brasil
        const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;
        if (isBRL && selectedCountryCode === 'BR' && !formData.installments) {
            errorFields.push(addError('installments', t('checkout.errors.requiredInstallments')));
        }

        setErrors(newErrors);

        // Se houver campos com erro, rolar até o primeiro
        if (errorFields.length > 0) {
            setTimeout(() => {
                const firstErrorField = document.getElementById(errorFields[0]);
                if (firstErrorField) {
                    firstErrorField.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Opcional: adicionar foco ao elemento
                    firstErrorField.focus();
                }
            }, 100);

            return false;
        }

        // Se não houver erros, retorna true
        return true;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        // Notificar que o pagamento está sendo processado
        if (onPaymentProcessing) {
            onPaymentProcessing();
        }


        try {
            // CORREÇÃO CRÍTICA: Calcular o valor diretamente das opções de parcelamento
            // em vez de usar as props que podem estar dessincronizadas
            const isBRL = offerData?.currency === 'BRL' || !offerData?.currency;
            const installmentsValue = isBRL ? parseInt(formData.installments) : 1;

            // Buscar o valor correto da opção selecionada
            let finalAmount = 0;
            let currentInstallmentValue = 0;

            // CORREÇÃO: Usar função helper para obter opções atualizadas
            const optionsToUse = getCurrentInstallmentOptions();

            if (optionsToUse?.length > 0) {
                const selectedOption = optionsToUse.find((opt: any) =>
                    opt.number === installmentsValue
                );

                if (selectedOption) {
                    finalAmount = selectedOption.totalAmount;
                    currentInstallmentValue = selectedOption.value;
                } else {
                    console.error('❌ ERRO CRÍTICO: Opção de parcelamento não encontrada!', {
                        installmentsValue,
                        availableOptions: optionsToUse.map((opt: any) => ({ number: opt.number, total: opt.totalAmount }))
                    });
                    throw new Error('Erro interno: opção de parcelamento inválida. Tente novamente.');
                }
            } else {
                console.error('❌ ERRO CRÍTICO: Nenhuma opção de parcelamento disponível!');
                throw new Error('Erro interno: dados de parcelamento indisponíveis. Recarregue a página.');
            }

            // Obter cookies do Facebook para melhorar atribuição
            const facebookCookies = getFacebookCookies();
            const countryCodeForBackend = selectedCountryCode === 'BR' ? 'br' : 'intl';

            const paymentData = {
                sessionId: sessionId || localStorage.getItem(`sessionId_${offerId}`),
                companyId: offerData?.product?.company?.id || 'null',
                paymentMethod: selectedMethod,
                amount: finalAmount,
                countryCode: countryCodeForBackend,
                couponDiscount: couponDiscount > 0 ? couponDiscount : undefined, // Incluir o desconto nos dados
                couponCode: appliedCoupon?.code || undefined, // Incluir o código do cupom se aplicado
                customerName: formData.name,
                customerEmail: formData.email,
                customerPhone: formData.phone,
                customerDocument: selectedCountryCode === 'BR' ? formData.documentNumber.replace(/\D/g, '') : undefined,
                installments: installmentsValue,
                ip: clientIp || localStorage.getItem('spip') || '',
                tracking: utmParams,
                offerId: offerId,
                // Cookies do Facebook para atribuição
                fbp: facebookCookies.fbp,
                fbc: getFbc(),
                // URL do checkout: event_source_url do Purchase na API de Conversões
                sourceUrl: window.location.href,
                cardData: selectedMethod === 'card' ? {
                    cardNumber: cardNumber.replace(/\s/g, ''),
                    holderName: cardName,
                    expiryDate: cardExpiry,
                    cvv: cardCvv
                } : undefined,
                items: [
                    {
                        productId: offerData?.product?.id,
                        title: offerData?.name || 'Produto',
                        quantity: 1,
                        price: offerData?.price || 0,
                        id: offerData?.id || offerId
                    }
                ],
                invoiceName: offerData?.product?.supportInfo?.invoiceName || 'Produto',
                selectedBumps: selectedBumps.length > 0 ? selectedBumps : undefined, // Incluir os bumps selecionados
                termsAccepted: hasCompanyTerms ? true : undefined // Aceite dos termos do vendedor
            };

            // VALIDAÇÕES FINAIS DE SEGURANÇA
            const expectedInstallments = parseInt(formData.installments);

            // 1. Verificar consistência do parcelamento
            if (paymentData.installments !== expectedInstallments) {
                console.error('❌ ERRO: Inconsistência detectada no parcelamento!', {
                    formInstallments: formData.installments,
                    paymentInstallments: paymentData.installments,
                    expectedInstallments: expectedInstallments
                });
                throw new Error('Erro interno: inconsistência no parcelamento detectada. Tente novamente.');
            }

            // 2. Verificar se o valor não é zero
            if (paymentData.amount <= 0) {
                console.error('❌ ERRO: Valor de pagamento inválido!', {
                    amount: paymentData.amount,
                    installments: paymentData.installments
                });
                throw new Error('Erro interno: valor de pagamento inválido. Tente novamente.');
            }

            // 3. Log final para auditoria
            console.log('✅ PAGAMENTO VALIDADO - Dados finais:', {
                installments: paymentData.installments,
                amount: paymentData.amount,
                installmentValue: currentInstallmentValue,
                paymentMethod: paymentData.paymentMethod,
                timestamp: new Date().toISOString()
            });

            // Obter headers com event_id para dedupe com CAPI
            const eventIdHeaders = sessionId ? getCheckoutSessionHeaders(sessionId) : {};

            // Chamada para a API
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/payments`,
                paymentData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...eventIdHeaders, // Adicionar event_id no header
                    },
                    // Pagamentos podem levar mais tempo; manter 65s aqui
                    timeout: 65000
                }
            );

            // Verificar se a resposta foi bem-sucedida
            if (response.status === 201 || response.status === 200) {
                // Verificar se o pagamento é PIX com status pending_payment
                if (selectedMethod === 'pix') {
                    // Obter o orderId diretamente da resposta
                    const orderId = response.data.data?.orderId || '';

                    // Redirecionar para a página de finalização do PIX incluindo o orderId como path parameter
                    window.location.href = `/finalization-pix/${orderId}`;
                    return; // Importante para evitar que continue a execução
                }

                // Verificar se o pagamento é BOLETO com status pending
                // API retorna status: "error" mas com dados do boleto quando está aguardando pagamento
                if (selectedMethod === 'boleto' &&
                    response.data.data?.orderId &&
                    (response.data.data?.paymentStatus === 'pending' ||
                        response.data.data?.orderStatus === 'pending_payment')) {
                    // Obter o orderId diretamente da resposta
                    const orderId = response.data.data.orderId;

                    // Redirecionar para a página de finalização do BOLETO incluindo o orderId como path parameter
                    window.location.href = `/finalization-boleto/${orderId}`;
                    return; // Importante para evitar que continue a execução
                }

                // Verificar se o pagamento foi aprovado com base na resposta padronizada da API
                else if (
                    // Caso 1: Formato principal - status da resposta é success
                    (response.data.status === "success" &&
                        response.data.data &&
                        (
                            response.data.data.paymentStatus === "approved" ||
                            response.data.data.orderStatus === "paid"
                        )) ||
                    // Caso 2: Formato alternativo com status="error" mas orderStatus="paid"
                    (response.data.status === "error" &&
                        response.data.data &&
                        response.data.data.orderStatus === "paid")
                ) {
                    // Chamar o callback de sucesso
                    onSuccess(response.data);
                } else {
                    // Se não corresponder a nenhum caso de sucesso, tratar como erro
                    // Usar a mensagem específica da Getnet se disponível
                    const gatewayMessage = response.data.data?.message;
                    const errorMessage = gatewayMessage || t('checkout.errors.paymentDeclined');
                    throw new Error(errorMessage);
                }
            } else {
                // Se o status não for 201 ou 200, tratar como erro
                throw new Error(t('checkout.processing.processingError'));
            }
        } catch (error: any) {
            console.error(t('checkout.processing.serverError'), error);

            // Tentar extrair mensagem específica da API em caso de erro HTTP
            if (error.response?.data) {
                const apiErrorMessage = error.response.data.data?.message ||
                    error.response.data.message ||
                    error.message;
                const enhancedError = new Error(apiErrorMessage);
                onError(enhancedError);
            } else {
                // Chamar o callback de erro com o erro original
                onError(error);
            }
        } finally {
            // Garantir que o estado de submissão seja redefinido em todos os casos
            setIsSubmitting(false);
        }
    };

    // Lista de países filtrada pelo termo de busca
    const filteredCountries = countries.filter((country) => {
        if (!countrySearch.trim()) return true;
        const term = countrySearch.toLowerCase();
        return (
            country.name.toLowerCase().includes(term) ||
            country.code.toLowerCase().includes(term)
        );
    });

    return (
        <div className="grid grid-cols-1 px-3 pb-3 pt-0">
            <div className="col-span-1">
                <CheckoutCard className="space-y-6">
                    {apiError && (
                        <div className="bg-red-50 p-3 rounded-lg mb-4">
                            {apiError}
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="bg-green-50 p-3 rounded-lg mb-4">
                            {apiSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* Resumo do Produto / Oferta (topo) + seletor de país */}
                        <div className="mb-4 mt-0">
                            {/* Seletor de país em linha própria para não espremer o nome do produto */}
                            <div className="flex justify-end mb-2">
                                    <div className="relative inline-flex items-center gap-1 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-200">
                                        {/* Trigger compacto: bandeira do país atual + texto */}
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1 focus:outline-none"
                                            onClick={() => setIsCountryDropdownOpen(prev => !prev)}
                                        >
                                            <span>{getFlagEmoji(selectedCountryCode)}</span>
                                            <span className="whitespace-nowrap">{isCountryDropdownOpen ? 'Fechar' : 'Alterar país'}</span>
                                            <span className="text-[9px] leading-none">{isCountryDropdownOpen ? '▴' : '▾'}</span>
                                        </button>

                                        {/* Dropdown avançado de países */}
                                        {isCountryDropdownOpen && (
                                            <div className="absolute right-0 top-full mt-1 w-92 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                                                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                                                    <input
                                                        type="text"
                                                        value={countrySearch}
                                                        onChange={(e) => setCountrySearch(e.target.value)}
                                                        placeholder="Buscar país"
                                                        className="w-full px-3 py-1.5 text-[11px] rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none"
                                                    />
                                                </div>
                                                <div className="max-h-72 overflow-y-auto py-1 text-[11px]">
                                                    {filteredCountries.map((country) => {
                                                        const parts = country.name.split('|');
                                                        const englishName = parts[0]?.trim() || country.name;
                                                        const localName = parts[1]?.trim();

                                                        return (
                                                            <button
                                                                key={country.code}
                                                                type="button"
                                                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 ${country.code === selectedCountryCode ? 'bg-gray-100 dark:bg-gray-800 font-semibold' : ''}`}
                                                                onClick={() => {
                                                                    if (country.code !== selectedCountryCode) {
                                                                        setIsCountryChanging(true);
                                                                        setSelectedCountryCode(country.code);
                                                                    }
                                                                    setIsCountryDropdownOpen(false);
                                                                    setCountrySearch('');
                                                                }}
                                                            >
                                                                <span>{getFlagEmoji(country.code)}</span>
                                                                <span>{englishName}</span>
                                                                {localName && (
                                                                    <>
                                                                        <span>|</span>
                                                                        <span>{localName}</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                    {filteredCountries.length === 0 && (
                                                        <div className="px-2 py-2 text-gray-400">
                                                            Nenhum país encontrado
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                            </div>

                            <div className="flex items-start">
                                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                    {/* Imagem do produto */}
                                    <div className="w-18 h-18 sm:w-21 sm:h-21 rounded-md overflow-hidden border border-gray-200 dark:border-gray-800 flex-shrink-0">
                                        {/* eslint-disable-next-line @next/next/no-img-element */}
                                        <img
                                            src={offerData?.product?.imageUrl}
                                            alt={offerData?.product?.name || offerData?.name || "Produto"}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* Informações principais */}
                                    <div className="flex-1 min-w-0">
                                        <p
                                            className="text-gray-900 dark:text-white font-semibold text-sm sm:text-base leading-snug line-clamp-2 break-words"
                                            title={offerData?.product?.name}
                                        >
                                            {offerData?.product?.name}
                                        </p>
                                        <div className="mt-0.5">
                                            <span className="block text-blue-600 font-bold text-base sm:text-lg leading-tight">
                                                {(() => {
                                                    const isRecurring = offerData?.billingType === 'recurring' || !!offerData?.frequency;
                                                    const freqLabel = isRecurring ? (getFrequencyLabel(offerData?.frequency) || getFrequencyLabel((offerData as any)?.billingCycle)) : '';

                                                    // CORREÇÃO: Usar função helper para obter opções atualizadas
                                                    const optionsToUse = getCurrentInstallmentOptions();

                                                    const currentOption = optionsToUse?.find((opt: any) =>
                                                        opt.number === parseInt(formData.installments)
                                                    );

                                                    const displayTotalValue = currentOption?.totalAmount ?? totalValue ?? 0;
                                                    const displayInstallmentValue = currentOption?.value ?? installmentValue ?? 0;

                                                    // Preço de ancoragem da oferta (pode vir como string ou número)
                                                    const rawAnchoringPrice = offerData?.anchoringPrice;
                                                    const anchoringPrice = rawAnchoringPrice !== undefined && rawAnchoringPrice !== null
                                                        ? Number(rawAnchoringPrice)
                                                        : 0;

                                                    // Espaço não-quebrável dentro do valor para "R$" não ficar numa linha e o número em outra
                                                    const money = (v: number) => formatCurrency(v, currencyCode).replace(/\s/g, ' ');

                                                    let mainText: string;

                                                    if (formData.installments === "1") {
                                                        // Só o preço no resumo do produto (o "À vista" continua no seletor de parcelas)
                                                        const base = money(displayTotalValue);
                                                        mainText = freqLabel ? `${base} / ${freqLabel}` : base;
                                                    } else {
                                                        const parts = `${formData.installments}x de ${money(displayInstallmentValue)}*`;
                                                        mainText = freqLabel ? `${parts} / ${freqLabel}` : parts;
                                                    }

                                                    // Se houver preço de ancoragem e ele for diferente do valor atual,
                                                    // exibe "De X" acima e o texto principal abaixo
                                                    if (anchoringPrice > 0 && displayTotalValue !== anchoringPrice) {
                                                        const fromPrice = money(anchoringPrice);
                                                        return (
                                                            <>
                                                                <span className="flex flex-wrap items-baseline gap-x-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                                                                    <span className="line-through whitespace-nowrap">De {fromPrice}</span>
                                                                    <span className="whitespace-nowrap">por:</span>
                                                                </span>
                                                                <span className="block">{mainText}</span>
                                                            </>
                                                        );
                                                    }

                                                    return mainText;
                                                })()}
                                            </span>
                                        </div>

                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug line-clamp-2 break-words">
                                            {('Autor:')} {offerData?.product?.supportInfo?.invoiceName || offerData?.product?.company?.fantasyName || 'Não informado'}
                                        </p>

                                    </div>
                                </div>

                            </div>
                            <hr className="mt-4 border-gray-200 dark:border-gray-800" />
                        </div>

                        {/* Contato */}

                        {/* Skeleton loader ao mudar país */}
                        {isCountryChanging ? (
                            <div className="mt-3 space-y-3 animate-pulse">
                                <div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2"></div>
                                    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                                <div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2"></div>
                                    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2"></div>
                                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    </div>
                                    <div>
                                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2"></div>
                                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mt-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">{t('checkout.name')}</p>
                                    <div className="relative">
                                        <Input
                                            theme="light"
                                            type="text"
                                            id="inputName"
                                            placeholder={t('checkout.namePlaceholder')}
                                            className={`pl-[62px] ${errors.inputName ? 'border-red-500' : ''}`}
                                            value={formData.name}
                                            onChange={handleNameChange}
                                            onBlur={handleNameBlur}
                                        />
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 z-10">
                                            <User className="size-4" />
                                        </span>
                                    </div>
                                    {errors.inputName && <p className="mt-1 text-sm text-red-500">{errors.inputName}</p>}
                                </div>

                                <div className="mt-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">{t('checkout.email')}</p>
                                    <div className="relative">
                                        <Input
                                            theme="light"
                                            placeholder={t('checkout.emailPlaceholder')}
                                            type="email"
                                            id="email"
                                            className={`pl-[62px] ${errors.email ? 'border-red-500' : ''}`}
                                            value={formData.email}
                                            onChange={handleEmailChange}
                                            onBlur={handleEmailBlur}
                                        />
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 z-10">
                                            <Mail className="size-4" />
                                        </span>
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                </div>

                                <div className={`grid mt-3 gap-4 ${selectedCountryCode === 'BR' ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                                    {/* Campo de documento - apenas para Brasil */}
                                    {selectedCountryCode === 'BR' && (
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">{t('checkout.document')}</p>
                                            <div className="relative">
                                                <Input
                                                    theme="light"
                                                    type="text"
                                                    id="inputDocumentNumber"
                                                    placeholder={t('checkout.documentPlaceholder')}
                                                    className={`pl-[62px] ${errors.inputDocumentNumber ? 'border-red-500' : ''}`}
                                                    value={formData.documentNumber}
                                                    onChange={handleDocumentChange}
                                                    onBlur={handleDocumentBlur}
                                                    maxLength={18} // Tamanho máximo para CNPJ com máscara
                                                />
                                                <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 z-10">
                                                    <IdCard className="size-4" />
                                                </span>
                                            </div>
                                            {errors.inputDocumentNumber && <p className="mt-1 text-sm text-red-500">{errors.inputDocumentNumber}</p>}
                                        </div>
                                    )}

                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">{t('checkout.phone')}</p>
                                        <div className="relative">
                                            <PhoneInput
                                                id="phone"
                                                defaultCountry="BR"
                                                countryCode={selectedCountryCode}
                                                value={formData.phone}
                                                onBlur={handlePhoneBlur}
                                                onChange={(value) => {
                                                    setFormData({
                                                        ...formData,
                                                        phone: value || "",
                                                    });
                                                }}
                                            />
                                        </div>
                                        {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                                    </div>
                                </div>
                                {/* Fim do Contato */}

                                {/* Cupom de Desconto - Collapsável (só exibe se product.enableCoupon === true) */}
                                {offerData?.product?.enableCoupon && (
                                    <div className="mt-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                        {/* Header do cupom - collapsável */}
                                        <button
                                            type="button"
                                            onClick={() => setIsCouponOpen(!isCouponOpen)}
                                            className="flex items-center justify-between w-full text-left text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                                                    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                                                    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                                                </svg>
                                                <span className="text-sm">Tem um cupom de desconto?</span>
                                            </div>
                                            <svg
                                                className={`w-5 h-5 transition-transform ${isCouponOpen ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Conteúdo collapsível */}
                                        {isCouponOpen && (
                                            <div className="mt-5 space-y-2">
                                                <div className="flex gap-3 items-start">
                                                    <div className="flex-1">
                                                        <Input
                                                            theme="light"
                                                            type="text"
                                                            placeholder="Digite o código"
                                                            value={couponCode}
                                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                            disabled={Boolean(appliedCoupon) || couponLoading}
                                                            className={`w-full ${appliedCoupon ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleApplyCoupon}
                                                        disabled={couponLoading || (!appliedCoupon && !couponCode.trim())}
                                                        className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-colors ${appliedCoupon
                                                            ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                                            : couponCode.trim()
                                                                ? 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                                                            } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap`}
                                                        style={{ minWidth: '90px' }}
                                                    >
                                                        {couponLoading ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                        ) : (
                                                            appliedCoupon ? 'Remover' : 'Aplicar'
                                                        )}
                                                    </button>
                                                </div>

                                                {/* Mensagens de erro/sucesso */}
                                                {couponError && (
                                                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <p className="text-sm text-red-700">{couponError}</p>
                                                    </div>
                                                )}
                                                {couponSuccess && (
                                                    <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <p className="text-sm text-green-700">{couponSuccess}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Pagamento */}

                                <div className="grid grid-cols-1 gap-6 mt-6">
                                    {/* Início do Componente de Métodos de Pagamento (Tabs) */}
                                    <div className="overflow-hidden">
                                        {/* Barra de abas (segmented tabs sem rádio) */}
                                        <div
                                            role="tablist"
                                            aria-label="Formas de pagamento"
                                            className="flex items-center gap-2 py-2"
                                        >
                                            {/* Cartão */}
                                            {offerData?.paymentMethods?.creditCard && (
                                                <button
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={selectedMethod === "card"}
                                                    onClick={() => handleMethodSelect("card")}
                                                    className={[
                                                        "relative",
                                                        "flex-1 md:flex-none md:min-w-[140px]",
                                                        "flex items-center justify-center gap-2",
                                                        "px-4 py-2 rounded-md border text-sm transition",
                                                        "focus:outline-none focus:ring-2 focus:ring-blue-500/40",
                                                        selectedMethod === "card"
                                                            ? "bg-white text-blue-700 border-blue-500 shadow-sm"
                                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                                                    ].join(" ")}
                                                >
                                                    <i className="ri-bank-card-line text-base not-italic"></i>
                                                    <span>{t("checkout.creditCard")}</span>

                                                    {selectedMethod === "card" && (
                                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center shadow">
                                                            <i className="ri-check-line text-[10px] leading-none"></i>
                                                        </span>
                                                    )}
                                                </button>
                                            )}

                                            {/* Pix - apenas para Brasil */}
                                            {offerData?.paymentMethods?.pix && selectedCountryCode === 'BR' && (
                                                <button
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={selectedMethod === "pix"}
                                                    onClick={() => handleMethodSelect("pix")}
                                                    className={[
                                                        "relative",
                                                        "flex-1 md:flex-none md:min-w-[140px]",
                                                        "flex items-center justify-center gap-2",
                                                        "px-4 py-2 rounded-md border text-sm transition",
                                                        "focus:outline-none focus:ring-2 focus:ring-blue-500/40",
                                                        selectedMethod === "pix"
                                                            ? "bg-white text-blue-700 border-blue-500 shadow-sm"
                                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                                                    ].join(" ")}
                                                >
                                                    <i className="ri-pix-line text-base not-italic"></i>
                                                    <span>Pix</span>

                                                    {selectedMethod === "pix" && (
                                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center shadow">
                                                            <i className="ri-check-line text-[10px] leading-none"></i>
                                                        </span>
                                                    )}
                                                </button>
                                            )}

                                            {/* Boleto - apenas para Brasil */}
                                            {offerData?.paymentMethods?.boleto && selectedCountryCode === 'BR' && (
                                                <button
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={selectedMethod === "boleto"}
                                                    onClick={() => handleMethodSelect("boleto")}
                                                    className={[
                                                        "relative",
                                                        "flex-1 md:flex-none md:min-w-[140px]",
                                                        "flex items-center justify-center gap-2",
                                                        "px-4 py-2 rounded-md border text-sm transition",
                                                        "focus:outline-none focus:ring-2 focus:ring-blue-500/40",
                                                        selectedMethod === "boleto"
                                                            ? "bg-white text-blue-700 border-blue-500 shadow-sm"
                                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                                                    ].join(" ")}
                                                >
                                                    <i className="ri-barcode-box-line text-base not-italic"></i>
                                                    <span>Boleto</span>

                                                    {selectedMethod === "boleto" && (
                                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center shadow">
                                                            <i className="ri-check-line text-[10px] leading-none"></i>
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                        </div>

                                        {/* Área de conteúdo do método selecionado (CONTEÚDOS ORIGINAIS) */}
                                        <div role="tabpanel" className="mt-3 p-4 border border-gray-200 rounded-lg">
                                            {/* === Cartão === */}
                                            {offerData?.paymentMethods?.creditCard && selectedMethod === "card" && (
                                                <>
                                                    <div className="mt-2 space-y-4">
                                                        <div className="grid grid-cols-12 gap-4">
                                                            {/* Número do cartão */}
                                                            <div className="col-span-12 md:col-span-6 order-1">
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">
                                                                    {t("checkout.cardNumber")}
                                                                </p>
                                                                <div className="relative mt-1">
                                                                    <Input
                                                                        theme="light"
                                                                        type="text"
                                                                        id="cardNumber"
                                                                        placeholder={t("checkout.cardNumberPlaceholder")}
                                                                        className={`pl-10 pr-10 w-full ${errors.cardNumber ? "border-red-500" : ""
                                                                            }`}
                                                                        value={cardNumber}
                                                                        onChange={handleCardNumberChange}
                                                                        onBlur={handleCardNumberBlur}
                                                                        maxLength={19}
                                                                    />
                                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                                                        <CreditCard className="w-4 h-4" />
                                                                    </span>
                                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                                                        <LockIcon className="w-4 h-4" />
                                                                    </span>
                                                                </div>
                                                                {errors.cardNumber && (
                                                                    <p className="mt-1 text-sm text-red-500">
                                                                        {errors.cardNumber}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* Validade */}
                                                            <div className="col-span-6 md:col-span-3 order-2 md:order-2">
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">
                                                                    {t("checkout.expiry")}
                                                                </p>
                                                                <div className="mt-1">
                                                                    <Input
                                                                        theme="light"
                                                                        type="text"
                                                                        id="cardExpiry"
                                                                        placeholder={t("checkout.expiryPlaceholder")}
                                                                        className={`w-full ${errors.cardExpiry ? "border-red-500" : ""
                                                                            }`}
                                                                        value={cardExpiry}
                                                                        onChange={handleCardExpiryChange}
                                                                        onBlur={handleCardExpiryBlur}
                                                                        maxLength={5}
                                                                    />
                                                                </div>
                                                                {errors.cardExpiry && (
                                                                    <p className="mt-1 text-sm text-red-500">
                                                                        {errors.cardExpiry}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* CVV */}
                                                            <div className="col-span-6 md:col-span-3 order-3 md:order-3">
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">
                                                                    {t("checkout.cvv")}
                                                                </p>
                                                                <div className="mt-1 relative">
                                                                    <Input
                                                                        theme="light"
                                                                        type="text"
                                                                        id="cardCvv"
                                                                        placeholder={t("checkout.cvvPlaceholder")}
                                                                        className={`w-full ${errors.cardCvv ? "border-red-500" : ""
                                                                            }`}
                                                                        value={cardCvv}
                                                                        onChange={handleCardCvvChange}
                                                                        onBlur={handleCardCvvBlur}
                                                                        maxLength={4}
                                                                    />
                                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                                                        {/* seu SVG do cadeado/cvv */}
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            width="16"
                                                                            height="16"
                                                                            viewBox="0 0 16 16"
                                                                            fill="none"
                                                                            className="h-6 w-6"
                                                                        >
                                                                            ...
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                                {errors.cardCvv && (
                                                                    <p className="mt-1 text-sm text-red-500">
                                                                        {errors.cardCvv}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* Nome no cartão */}
                                                            <div className="col-span-12 order-4 md:order-4">
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">
                                                                    {t("checkout.nameOnCard")}
                                                                </p>
                                                                <div className="mt-1">
                                                                    <Input
                                                                        theme="light"
                                                                        type="text"
                                                                        id="cardName"
                                                                        placeholder={t("checkout.nameOnCardPlaceholder")}
                                                                        className={`w-full ${errors.cardName ? "border-red-500" : ""
                                                                            }`}
                                                                        value={cardName}
                                                                        onChange={handleCardNameChange}
                                                                        onBlur={handleCardNameBlur}
                                                                    />
                                                                    {errors.cardName && (
                                                                        <p className="mt-1 text-sm text-red-500">
                                                                            {errors.cardName}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Parcelas (BRL) - Só exibe se maxInstallments > 1 e país for Brasil */}
                                                            {(offerData?.currency === "BRL" || !offerData?.currency) && offerData?.maxInstallments > 1 && selectedCountryCode === 'BR' && (
                                                                <div className="col-span-12 order-5 mb-3">
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2 truncate">
                                                                        {t("checkout.installments")}
                                                                    </p>
                                                                    <div className="mt-1 relative">
                                                                        <Select
                                                                            options={installmentsOptions}
                                                                            placeholder={installmentsOptions.length > 0 ? "" : t("checkout.installmentsPlaceholder")}
                                                                            onChange={handleInstallmentsChange}
                                                                            className={`dark:bg-dark-900 ${errors.installments ? "border-red-500" : ""
                                                                                }`}
                                                                            value={(() => {
                                                                                // PROTEÇÃO: PIX/Boleto sempre 1x
                                                                                if ((selectedMethod as string) === 'pix' || (selectedMethod as string) === 'boleto') {
                                                                                    return "1";
                                                                                }
                                                                                // Para cartão, usa seleção atual se válida
                                                                                if (formData.installments && installmentsOptions.some(opt => opt.value === formData.installments)) {
                                                                                    return formData.installments;
                                                                                }
                                                                                // Fallback: maior parcela ou 1x
                                                                                return installmentsOptions[installmentsOptions.length - 1]?.value || "1";
                                                                            })()}
                                                                            theme="light"
                                                                            dropUp={true}
                                                                            readOnly={isCalculating}
                                                                        />
                                                                        {isCalculating && (
                                                                            <div className="absolute left-1 top-1/2 transform -translate-y-1/2">
                                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                                                                            </div>
                                                                        )}
                                                                        {errors.installments && (
                                                                            <p className="mt-1 text-sm text-red-500">
                                                                                {errors.installments}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* === PIX === */}
                                            {offerData?.paymentMethods?.pix && selectedMethod === "pix" && (
                                                <>
                                                    <div
                                                        id="pix-section"
                                                        className="mt-2 flex flex-col items-center justify-center py-4"
                                                    >
                                                        <div className="text-gray-600 mb-4">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="-252.3 356.1 163 80.9"
                                                                style={{
                                                                    width: "160px",
                                                                    height: "80px",
                                                                    marginBottom: "24px",
                                                                    color: "#666",
                                                                }}
                                                            >
                                                                <path
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeMiterlimit="10"
                                                                    strokeWidth="2"
                                                                    d="M-108.9 404.1v30c0 1.1-.9 2-2 2H-231c-1.1 0-2-.9-2-2v-75c0-1.1.9-2 2-2h120.1c1.1 0 2 .9 2 2v37m-124.1-29h124.1"
                                                                />
                                                                <circle cx="-227.8" cy="361.9" r="1.8" fill="currentColor" />
                                                                <circle cx="-222.2" cy="361.9" r="1.8" fill="currentColor" />
                                                                <circle cx="-216.6" cy="361.9" r="1.8" fill="currentColor" />
                                                                <path
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeMiterlimit="10"
                                                                    strokeWidth="2"
                                                                    d="M-128.7 400.1H-92m-3.6-4.1 4 4.1-4 4.1"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <p className="text-center text-sm text-gray-600">
                                                            Após o pagamento, você receberá por email as informações de
                                                            acesso à sua compra
                                                        </p>
                                                    </div>
                                                </>
                                            )}

                                            {/* === Boleto === */}
                                            {offerData?.paymentMethods?.boleto && selectedMethod === "boleto" && (
                                                <>
                                                    <div
                                                        className={`grid grid-cols-1 ${offerData?.paymentMethods?.pix ? "md:grid-cols-3" : "md:grid-cols-2"
                                                            } gap-4 mt-2`}
                                                    >
                                                        <div className="border rounded-lg p-4">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="24"
                                                                height="24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="mb-2"
                                                            >
                                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                                            </svg>
                                                            <h3 className="text-md font-semibold mb-1">
                                                                Pague até a data de vencimento
                                                            </h3>
                                                            <p className="text-sm text-gray-600">
                                                                Faça o pagamento até a data de vencimento e garanta seu acesso
                                                                ao produto.
                                                            </p>
                                                        </div>

                                                        <div className="border rounded-lg p-4">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="24"
                                                                height="24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="mb-2"
                                                            >
                                                                <circle cx="12" cy="12" r="10"></circle>
                                                                <polyline points="12 6 12 12 16 14"></polyline>
                                                            </svg>
                                                            <h3 className="text-md font-semibold mb-1">
                                                                Aguarde a aprovação do pagamento
                                                            </h3>
                                                            <p className="text-sm text-gray-600">
                                                                Pagamentos com boleto levam até 3 dias úteis para serem
                                                                compensados.
                                                            </p>
                                                        </div>

                                                        {offerData?.paymentMethods?.pix && (
                                                            <div
                                                                className="bg-blue-50 border rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                                                                onClick={() => {
                                                                    setSelectedMethod("pix");
                                                                    setTimeout(() => {
                                                                        const pixSection = document.getElementById("pix-section");
                                                                        if (pixSection) {
                                                                            setSelectedMethod("pix");
                                                                            pixSection.scrollIntoView({
                                                                                behavior: "smooth",
                                                                                block: "center",
                                                                            });
                                                                        }
                                                                    }, 100);
                                                                }}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="24"
                                                                    height="24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    className="mb-2 text-blue-500"
                                                                >
                                                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                                    <line x1="12" y1="9" x2="12" y2="13"></line>
                                                                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                                                </svg>
                                                                <h3 className="text-md font-semibold mb-1 text-blue-500">
                                                                    Deseja ter acesso imediato ao produto?
                                                                </h3>
                                                                <p className="text-sm text-gray-600">
                                                                    Faça o pagamento via Pix{" "}
                                                                    <span className="text-blue-500 font-medium">clicando aqui</span>.
                                                                    Leva apenas alguns segundos para ser processado.
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {/* Fim do Componente de Métodos de Pagamento */}
                                </div>



                                {/* OrderBumps */}
                                <div className="grid grid-cols-1 gap-6 mt-2">
                                    {orderBumps.length > 0 && (
                                        <>
                                            {(() => {
                                                const filteredBumps = orderBumps.filter((bump: any) =>
                                                    // Mostrar se for um bump global ativo OU específico ativo para esta oferta
                                                    (bump.type === 'global' && bump.settingsData.active) ||
                                                    (bump.type === 'specific' && bump.settingsData.offerVariantId === offerData?.id && bump.settingsData.active)
                                                )
                                                    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

                                                // Só mostrar o título e os bumps se houver algum após filtragem
                                                return filteredBumps.length > 0 ? (
                                                    <>
                                                        <h3 className="font-semibold text-gray-800 mt-6">{t('checkout.takeTogether')}</h3>
                                                        {filteredBumps.map((bump: any) => (
                                                            <div
                                                                key={bump.id}
                                                                className="p-4 rounded-lg transition-all duration-300 w-full"
                                                                style={{
                                                                    border: `2px dashed ${bump.primaryColor}`,
                                                                    backgroundColor: getSuperLightColor(bump.primaryColor),
                                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                                                }}
                                                            >
                                                                <h3
                                                                    className="text-start font-semibold mb-2"
                                                                    style={{ color: bump.primaryColor }}
                                                                >
                                                                    {bump.settingsData.offerTitle}
                                                                </h3>

                                                                <p className="text-start text-sm mb-4 text-gray-800" style={{ whiteSpace: 'pre-line' }}>
                                                                    {bump.description}
                                                                </p>

                                                                <hr className="my-3 border-gray-300" />

                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                                    {/* Coluna 1 - Produto */}
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-16 h-16 rounded flex items-center justify-center">
                                                                            {bump.productData?.imageUrl ? (
                                                                                <img
                                                                                    src={bump.productData.imageUrl}
                                                                                    alt={bump.productData.name}
                                                                                    className="w-full h-full object-cover rounded"
                                                                                />
                                                                            ) : (
                                                                                <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                                                                                    <ShoppingCart size={24} className="text-gray-500" />
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <div className="flex flex-col justify-center">
                                                                            <h4 className="font-semibold text-gray-800">
                                                                                {bump.productData.name}
                                                                            </h4>

                                                                            <div className="flex flex-row gap-1 items-center flex-wrap">
                                                                                {(() => {
                                                                                    const anchoringPrice = bump.anchoringPrice ? parseFloat(bump.anchoringPrice) : 0;
                                                                                    const offerPrice = bump.offerPrice ? parseFloat(bump.offerPrice) : 0;

                                                                                    // Se os preços são iguais ou não há preço promocional, mostrar apenas um preço
                                                                                    if (!offerPrice || offerPrice <= 0 || anchoringPrice === offerPrice) {
                                                                                        return anchoringPrice > 0 && (
                                                                                            <p className="text-sm">
                                                                                                <span className="font-bold" style={{ color: bump.primaryColor }}>
                                                                                                    {formatCurrency(anchoringPrice, currencyCode)}
                                                                                                </span>
                                                                                            </p>
                                                                                        );
                                                                                    }

                                                                                    // Preços diferentes - mostrar "de/por"
                                                                                    return (
                                                                                        <>
                                                                                            {anchoringPrice > 0 && (
                                                                                                <p className="text-sm text-gray-500">de <span className="line-through text-gray-500">
                                                                                                    {formatCurrency(anchoringPrice, currencyCode)}
                                                                                                </span>
                                                                                                </p>
                                                                                            )}
                                                                                            {' '}
                                                                                            <p className="text-sm text-gray-500">por <span className="font-bold" style={{ color: bump.primaryColor }}>
                                                                                                {formatCurrency(offerPrice, currencyCode)}
                                                                                            </span>
                                                                                            </p>
                                                                                        </>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Coluna 2 - Vídeo (apenas se videoActive for true) */}
                                                                    {bump.videoActive && (
                                                                        <div className="flex items-center justify-center bg-gray-100 rounded-lg p-2">
                                                                            {bump.videoUrl ? (
                                                                                <div className="w-full aspect-video">
                                                                                    <iframe
                                                                                        src={bump.videoUrl}
                                                                                        className="w-full h-full rounded"
                                                                                        frameBorder="0"
                                                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                                        allowFullScreen
                                                                                    ></iframe>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="flex flex-col items-center justify-center p-4 text-center">
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mb-2 text-gray-400">
                                                                                        <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                                                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                                                                    </svg>
                                                                                    <p className="text-sm text-gray-500">{t('checkout.videoNotAvailable')}</p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Checkbox com fundo mais claro */}
                                                                <div
                                                                    className={`mt-4 p-2 rounded cursor-pointer ${isCalculating && processingBumpId === bump.id ? 'opacity-70 pointer-events-none' : ''}`}
                                                                    style={{
                                                                        backgroundColor: getLightColor(bump.primaryColor),
                                                                        border: `1px solid ${bump.primaryColor}`
                                                                    }}
                                                                    onClick={() => !(isCalculating && processingBumpId === bump.id) && toggleBumpSelection(bump.id)}
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="w-4 h-4"
                                                                            checked={selectedBumps.includes(bump.id)}
                                                                            readOnly
                                                                            style={{
                                                                                accentColor: bump.primaryColor,
                                                                                borderColor: bump.primaryColor
                                                                            }}
                                                                        />
                                                                        {isCalculating && processingBumpId === bump.id ? (
                                                                            <div className="flex items-center">
                                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" style={{ borderColor: bump.textColor }}></div>
                                                                                <span style={{ color: bump.textColor }}>
                                                                                    {isAddingBump ? t('checkout.addingToPurchase') : t('checkout.removingToPurchase')}
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <span style={{ color: bump.textColor }}>
                                                                                {bump.ctaText}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : null;
                                            })()}
                                        </>
                                    )}
                                </div>

                                {/* Resumo da compra */}
                                <div className="mt-6">
                                    <h3 className="font-semibold text-gray-800 mt-6">
                                        {t('checkout.orderSummary')}
                                    </h3>

                                    {/* Mostrar o desconto se houver */}
                                    {couponDiscount > 0 && (
                                        <div className="flex items-center justify-between mt-4 text-red-500">
                                            <span className="text-sm">{t('checkout.appliedDiscount')}</span>
                                            <span className="text-sm font-semibold">
                                                {appliedCouponData && appliedCouponData.discountType === "percentage"
                                                    ? `-${appliedCouponData.discountValue}% (${formatCurrency(couponDiscount, currencyCode)})`
                                                    : `-${formatCurrency(couponDiscount, currencyCode)}`
                                                }
                                            </span>
                                        </div>
                                    )}

                                    {/* Exibir order bumps selecionados */}
                                    {selectedBumps.length > 0 && orderBumps.length > 0 && (
                                        <div className="mt-2 space-y-2">
                                            <div className="flex items-center justify-between mt-6 mb-3">
                                                <span className="text-gray-700">{offerData?.name}</span>
                                                <span className="text-gray-600">
                                                    {formatCurrency(parseFloat(String(offerData?.price || 0)), currencyCode)}
                                                </span>
                                            </div>
                                            {selectedBumps.map(bumpId => {
                                                const bump = orderBumps.find((b: any) => b.id === bumpId);
                                                if (!bump) return null;

                                                return (
                                                    <div key={bumpId} className="flex items-center justify-between mb-3">
                                                        <span className="text-gray-700">{bump.name}</span>
                                                        <span className="text-gray-600">
                                                            {formatCurrency(parseFloat(String(bump.offerPrice || 0)), currencyCode)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className="font-semibold text-gray-800 mt-3 mb-1">{t('checkout.totalLabel')}</h3>
                                        <span className="text-gray-600">
                                            {(() => {
                                                const isRecurring = offerData?.billingType === 'recurring' || !!offerData?.frequency;
                                                const freqLabel = isRecurring ? (getFrequencyLabel(offerData?.frequency) || getFrequencyLabel((offerData as any)?.billingCycle)) : '';

                                                // CORREÇÃO: Usar função helper para obter opções atualizadas (igual ao submit)
                                                const optionsToUse = getCurrentInstallmentOptions();

                                                const currentOption = optionsToUse?.find((opt: any) =>
                                                    opt.number === parseInt(formData.installments)
                                                );

                                                const summaryTotalValue = currentOption?.totalAmount ?? totalValue ?? 0;
                                                const summaryInstallmentValue = currentOption?.value ?? installmentValue ?? 0;

                                                if (formData.installments === "1") {
                                                    // Só o valor no total (sem "À vista")
                                                    const base = formatCurrency(summaryTotalValue, currencyCode);
                                                    return freqLabel ? `${base} / ${freqLabel}` : base;
                                                }

                                                const parts = `${formData.installments}x de ${formatCurrency(summaryInstallmentValue, currencyCode)}*`;
                                                return freqLabel ? `${parts} / ${freqLabel}` : parts;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                                {/* Fim do Resumo da compra */}

                                {/* Termos de uso do vendedor */}
                                {hasCompanyTerms && (
                                    <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={termsAccepted}
                                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                                className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 shrink-0 cursor-pointer"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Li e aceito os{' '}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setTermsModalOpen(true);
                                                    }}
                                                    className="text-blue-600 font-semibold hover:text-blue-800 underline"
                                                >
                                                    {offerData.companyTerms.title || 'Termos de Uso'}
                                                </button>
                                            </span>
                                        </label>
                                    </div>
                                )}

                                {/* Modal dos termos de uso do vendedor */}
                                {termsModalOpen && offerData?.companyTerms && (
                                    <div
                                        className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50 p-4"
                                        onClick={() => setTermsModalOpen(false)}
                                    >
                                        <div
                                            className="w-full max-w-4xl max-h-[90vh] rounded-xl bg-white shadow-2xl flex flex-col"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {offerData.companyTerms.title}
                                                </h3>
                                                <button
                                                    type="button"
                                                    onClick={() => setTermsModalOpen(false)}
                                                    className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            </div>
                                            <div
                                                className="px-8 py-6 overflow-y-auto terms-content"
                                                dangerouslySetInnerHTML={{ __html: offerData.companyTerms.content }}
                                            />
                                            <div className="border-t border-gray-200 px-6 py-4 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setTermsAccepted(true);
                                                        setTermsModalOpen(false);
                                                    }}
                                                    className="w-full rounded-lg bg-green-700 px-4 py-3 text-sm font-semibold text-white hover:bg-green-800"
                                                >
                                                    Li e aceito os termos
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Botão de finalizar compra */}
                                <div className="mt-6 mb-6">
                                    <Button
                                        size="lg"
                                        className="w-full bg-green-700 text-white shadow-theme-xs hover:bg-green-800 disabled:bg-green-700 font-bold"
                                        disabled={isSubmitting || isCalculating || (hasCompanyTerms && !termsAccepted)}
                                    >
                                        {isCalculating ? t('checkout.updateValues') : t('checkout.completeOrder')}
                                    </Button>
                                </div>
                                {/* Fim do Botão de finalizar compra */}

                                <div className="mt-6 flex items-center justify-center gap-1 whitespace-nowrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="h-4 w-4 text-slate-400"><path fill="currentColor" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12c5.16-1.26 9-6.45 9-12V5l-9-4m0 6c1.4 0 2.8 1.1 2.8 2.5V11c.6 0 1.2.6 1.2 1.3v3.5c0 .6-.6 1.2-1.3 1.2H9.2c-.6 0-1.2-.6-1.2-1.3v-3.5c0-.6.6-1.2 1.2-1.2V9.5C9.2 8.1 10.6 7 12 7m0 1.2c-.8 0-1.5.5-1.5 1.3V11h3V9.5c0-.8.7-1.3 1.5-1.3Z"></path></svg>
                                    <p className="text-[11px] sm:text-xs text-gray-400">
                                        {t('checkout.secureData')}
                                    </p>
                                </div>

                                {/* Selo BaaS Asaas (Resolução Conjunta BCB nº 16/2025) */}
                                <AsaasFinancialNotice
                                    variant="positivo"
                                    notice={t('checkout.asaasNotice')}
                                    supportLabel={t('checkout.asaasSupport')}
                                    sealWidth={100}
                                    className="mt-3"
                                />
                            </>
                        )}
                    </form>
                </CheckoutCard>

                <div className="col-span-12">
                    <TermsAndConditions />
                </div>

            </div>

            {/* Estilos para formatacao do conteudo HTML dos termos */}
            <style>{`
                .terms-content {
                    font-size: 14px;
                    line-height: 1.7;
                    color: #374151;
                    word-break: break-word;
                }
                .terms-content h1 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin: 1.5rem 0 0.75rem;
                    color: #111827;
                }
                .terms-content h2 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin: 1.5rem 0 0.5rem;
                    color: #1f2937;
                }
                .terms-content h3 {
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin: 1.25rem 0 0.5rem;
                    color: #1f2937;
                }
                .terms-content p {
                    margin: 0.5rem 0;
                }
                .terms-content ul, .terms-content ol {
                    margin: 0.5rem 0;
                    padding-left: 1.5rem;
                }
                .terms-content ul {
                    list-style-type: disc;
                }
                .terms-content ol {
                    list-style-type: decimal;
                }
                .terms-content li {
                    margin: 0.25rem 0;
                }
                .terms-content strong, .terms-content b {
                    font-weight: 600;
                }
                .terms-content em, .terms-content i {
                    font-style: italic;
                }
                .terms-content a {
                    color: #2563eb;
                    text-decoration: underline;
                }
                .terms-content blockquote {
                    border-left: 3px solid #d1d5db;
                    padding-left: 1rem;
                    margin: 0.75rem 0;
                    color: #6b7280;
                }
            `}</style>
        </div >
    );
}